import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { BlueprintBase, CoverBlueprint } from './models/blueprint.model';
import { LocaleId } from './models/locale.model';
import {
  LovValues,
  PropertyGroupQuery,
  TemplateFieldsCatalog,
  ViewTemplate,
  ViewTemplateCatalog,
} from './models/template.model';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from './report-export-view.token';
import { refreshCacheForURLs } from './shared/constants/uris.const';
import { TEMPLATE_BLUEPRINTS } from './shared/templates/template-blueprints.const';
import { HttpMethods } from './shared/utils/http-methods.class';
import { Contact } from './views/presentation-dynamic-view/dynamic-contacts/dynamic-contacts.model';
import { StackingSlideQuery, StackingTenants } from './models/stackingplan.model';

@Injectable()
export class ReportExportViewService extends HttpMethods {
  private requestCache = {};
  private contacts: Contact[];
  private countries = [];
  private stackingPlanData: StackingTenants;

  constructor(@Inject(REPORT_EXPORT_CONFIGS_TOKEN) moduleConfig: ReportExportConfigurations, httpClient: HttpClient) {
    super(moduleConfig.baseUrl, httpClient);
  }

  getViewTemplateFieldsCatalog(uri: string): Observable<ViewTemplateCatalog> {
    if (uri == null) {
      throw new Error('No template fields URI');
    }
    return this.httpGet(uri);
  }

  getTemplateFieldsCatalog(
    catalogUri: string,
    templateId: number,
    reportLocaleId: number
  ): Observable<TemplateFieldsCatalog> {
    let uri: string = `${catalogUri}/${templateId}`;
    if (reportLocaleId) {
      uri = `${uri}?locale=${reportLocaleId}`;
    }
    return this.httpGet(uri);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   * @param viewId is a number contains the id of the view
   * @param selectedTemplate is of type {@link ViewTemplate} contains the selected template information.
   */
  saveTemplate(reportId: number, viewId: number, selectedTemplate: ViewTemplate) {
    return this.httpPut(`/reports/${reportId}/views/${viewId}/templates`, selectedTemplate);
  }

  // Temporary while a static file is still provided by BE
  getBaseBlueprintByTemplateId(templateId: number, entity: string, localeId: LocaleId): Observable<BlueprintBase> {
    let templateInfo = TEMPLATE_BLUEPRINTS[templateId];
    if (typeof templateInfo === 'object') {
      templateInfo = templateInfo[entity] || templateInfo[localeId];
    }
    return this.httpGet(`/static/${templateInfo}.json`);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   */
  getImages(reportId: number) {
    return this.httpGet(`/reports/${reportId}/images`);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   *  * @param viewID is a number contains the id of the view
   */
  getPropertyGroups(reportId: number, viewID: number) {
    return this.httpGet(`/reports/${reportId}/group/views/${viewID}`);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   *  * @param viewID is a number contains the id of the view
   */
  associatePropertyGroup(reportId: number, viewID: number, query: PropertyGroupQuery) {
    return this.httpPut(`/reports/${reportId}/group/views/${viewID}`, query);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   *  * @param viewID is a number contains the id of the view
   */
  deletePropertyGroup(reportId: number, viewID: number, associationID: number) {
    return this.httpDelete(`/reports/${reportId}/group/views/${viewID}`, { body: { associationID: associationID } });
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   * @param edpRecordId is a number contains the id of the record
   * @param imageId is a number contains the id of the selected image as a additional image
   */
  addAdditionalImage(reportId: number, requestBody: { edpRecordId: number; imageId: number }[]) {
    return this.httpPost(`/reports/${reportId}/images`, requestBody);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   * @param reportImageId is a number contains the id of the record
   * @param imageId is a number contains the id of the selected image as a additional image
   */
  changeAdditionalImage(reportId: number, reportImageId: number, imageId: number) {
    const requestBody = { reportImageId, imageId };
    return this.httpPut(`/reports/${reportId}/images`, requestBody);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   * @param reportImageId is a number contains the id of the reportImageId as a additional image
   */
  deleteAdditionalImage(reportId: number, reportImageId: number) {
    return this.httpDelete(`/reports/${reportId}/images/${reportImageId}`);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   */
  getContacts(reportId: number): Observable<Contact[]> {
    return this.httpGet(`/reports/${reportId}/contacts`).pipe(
      tap((values: Contact[]) => {
        this.setContacts(values);
      })
    );
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   *  * @param contactIds is an array of numbers contains the id of the contact
   */
  addContact(reportId: number, contactIds: number[]): Observable<Contact[]> {
    return this.httpPost(`/reports/${reportId}/contacts`, contactIds);
  }

  /**
   *
   * @param reportId is a number contains the id of the report
   *  * @param contactIds is an array of numbers contains the id of the contact
   */
  reOrderContacts(reportId: number, contactIds: number[]): Observable<Contact[]> {
    return this.httpPost(`/reports/${reportId}/contacts?replace=true`, contactIds);
  }

  /**
   *
   * @param reportId is an array of numbers contains the id of the report
   *  * @param contactIds is a number contains the id of the contact
   */
  removeContact(reportId: number, contactIds: number[]): Observable<Contact[]> {
    return this.httpDelete(`/reports/${reportId}/contacts`, { body: contactIds });
  }

  searchContact(query: string, country: string, cbreOnly: string): Observable<any> {
    return this.httpGet(`/reports/contacts?query=${query}${cbreOnly}${country}`);
  }

  setReportContactSetting(reportId: number, contactUpdate: Contact) {
    return this.httpPut(`/reports/${reportId}/contacts`, contactUpdate);
  }

  getLovObservablesData(
    lookup: string,
    reqPayLoad: string[] | string,
    localeId: number
  ): Observable<LovValues[]> | Observable<{}> {
    let result: Observable<LovValues[]> | Observable<{}>;
    const postLookupCheck = lookup.includes('POST') || lookup.includes('MIQ_EXTERNAL_RATING_TYPE');
    if (postLookupCheck) {
      const postLookup = lookup.split('POST:')[1];

      // TECH DEBT (Ramakrishna): harcode condition shouldn't be here. Need to move or refactor it.
      lookup = lookup.includes('MIQ_EXTERNAL_RATING_TYPE') ? '/ref/cascade/property/ratingLevel' : postLookup;
      const refreshCache: boolean = refreshCacheForURLs.includes(lookup);
      lookup = lookup.includes('/property/amenities') ? lookup : `${lookup}?locale=${localeId}`;
      if (!!reqPayLoad && Array.isArray(reqPayLoad)) {
        let lovData = {};
        reqPayLoad.forEach(req => {
          lovData[req] = this.retrieveLovValues(lookup, [req], refreshCache);
        });
        result = of(lovData);
      } else {
        result = this.retrieveLovValues(lookup, reqPayLoad, refreshCache);
      }
    } else {
      lookup = lookup.includes('?') ? `${lookup}&locale=${localeId}` : `${lookup}?locale=${localeId}`;
      result = this.getLovValues(lookup);
    }
    return result;
  }

  getCoverPageBluePrint(path: string): Observable<CoverBlueprint> {
    return this.httpGet(path) as Observable<CoverBlueprint>;
  }

  private cache(uri: string, request: () => Observable<LovValues[]>, refreshCache?: boolean) {
    if (this.requestCache[uri] && !refreshCache) {
      const isObservable = typeof this.requestCache[uri].subscribe === 'function';
      return isObservable ? this.requestCache[uri] : of(this.requestCache[uri]);
    }
    this.requestCache[uri] = request().pipe(
      tap(response => (this.requestCache[uri] = response)),
      share()
    );
    return this.requestCache[uri];
  }

  public getLovValues(uri: string): Observable<LovValues[]> {
    return this.cache(uri, this.httpGet.bind(this, uri));
  }

  public retrieveLovValues(uri: string, requestBody, refreshCache?: boolean) {
    return this.cache(uri, this.httpPost.bind(this, uri, requestBody), refreshCache);
  }
  retrieveContacts(): any {
    return this.contacts;
  }
  public getCountryList(): Observable<any[]> {
    if (this.countries && this.countries.length > 0) {
      return of(this.countries);
    }
    const uri = '/lookup/catalog/country';
    return this.httpGet(uri).pipe(tap(result => (this.countries = result as []))) as Observable<any[]>;
  }

  setContacts(contacts: Contact[]): void {
    this.contacts = contacts;
  }

  getStackingPlan(payload: StackingSlideQuery[]) {
    if (!this.stackingPlanData) {
      const uri = `/stack/nearTenants`;
      return this.httpPost(uri, payload).subscribe(data => {
        this.setStackingPlanData(data);
      });
    }
  }

  retrieveStackingPlanData(): StackingTenants {
    return this.stackingPlanData;
  }

  setStackingPlanData(stackingPlanData: StackingTenants): void {
    this.stackingPlanData = stackingPlanData;
  }
}
